import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchGuestAnnouncementByIdAction,
  fetchGuestAnnouncementsAction,
} from "../../ustils/actioncreator";
import { useAdminUi } from "../../hooks/useAdminUi";
import AnnouncementDetailPanel from "./AnnouncementDetailPanel";

function extractAnnouncementList(res) {
  if (!res || typeof res !== "object") return [];
  if (Array.isArray(res.data)) return res.data;
  if (res.data && typeof res.data === "object" && Array.isArray(res.data.data)) return res.data.data;
  if (Array.isArray(res.announcements)) return res.announcements;
  return [];
}

function extractTotalRecords(res, listLength) {
  const n = Number(res?.totalRecords ?? res?.data?.totalRecords);
  return Number.isFinite(n) && n >= 0 ? n : listLength;
}

function unwrapAnnouncementDetail(res) {
  if (!res || typeof res !== "object") return null;
  if (res._id) return res;
  const d = res.data;
  if (d && typeof d === "object" && !Array.isArray(d) && (d._id || d.id)) return d;
  return null;
}

function formatPropertyPrice(price, currency) {
  if (price == null || price === "") return "—";
  const n = Number(price);
  if (!Number.isFinite(n)) return String(price);
  const code = currency && String(currency).trim();
  if (code) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `${n.toLocaleString()} ${code}`;
    }
  }
  return n.toLocaleString();
}

function listingTypeLabel(type) {
  if (type === 1 || type === "1") return "For sale";
  if (type === 2 || type === "2") return "For rent";
  return "—";
}

function mapRow(item) {
  const id = item?._id || item?.id;
  const u = item?.user_id;
  const owner = typeof u === "object" && u !== null ? u : null;
  const ownerName = owner?.name || "—";
  const title = item?.property_name || item?.title || "—";
  const type = item?.property_type || "—";
  const location =
    item?.property_address ||
    [item?.property_area, item?.property_city, item?.property_country].filter(Boolean).join(", ") ||
    "—";
  const price = formatPropertyPrice(item?.price, item?.currency);
  const listing = listingTypeLabel(item?.listing_type);
  const dateRaw = item?.created_at || item?.createdAt;
  const dateListed = dateRaw
    ? new Date(dateRaw).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";
  return { id, title, ownerName, type, location, price, listing, dateListed, raw: item };
}

export default function GuestAnnouncementsList() {
  const {
    ui,
    listHeaderSx,
    listPaperSx,
    listTitleSx,
    statusChipSx,
    tableBodyCellSx,
    tableHeaderCellSx,
    tableRowHoverSx,
    tableSx,
    tableContainerSx,
  } = useAdminUi();

  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState(1);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailError, setDetailError] = useState("");

  const roleFilterButtonSx = (active) => ({
    textTransform: "none",
    fontSize: 13,
    minWidth: 96,
    px: 1.5,
    py: 0.6,
    borderRadius: 1.5,
    ...(active
      ? {
          bgcolor: ui.green,
          color: "#fff",
          borderColor: ui.green,
          "&:hover": { bgcolor: ui.greenDark || ui.green },
        }
      : {
          color: ui.inkMuted,
          borderColor: ui.borderLight,
          "&:hover": { borderColor: ui.green, color: ui.green },
        }),
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchGuestAnnouncementsAction({ page, perPage, user_role: userRole });
      const list = extractAnnouncementList(res);
      setRows(list.map(mapRow));
      setTotal(extractTotalRecords(res, list.length));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load guest announcements.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, userRole]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / perPage) || 1);
  const canGoNext = page < totalPages && rows.length > 0;

  const openDetail = async (id) => {
    if (!id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailRecord(null);
    setDetailError("");
    try {
      const raw = await fetchGuestAnnouncementByIdAction(id);
      setDetailRecord(unwrapAnnouncementDetail(raw) || raw?.data || null);
    } catch (e) {
      setDetailError(e?.response?.data?.message || e?.message || "Failed to load details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRecord(null);
    setDetailError("");
  };

  return (
    <>
      <Paper elevation={0} sx={listPaperSx}>
        <Box
          sx={{
            ...listHeaderSx,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
            <Typography sx={listTitleSx}>Guest announcements</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={userRole === 1 ? "contained" : "outlined"}
                onClick={() => {
                  setUserRole(1);
                  setPage(1);
                }}
                sx={roleFilterButtonSx(userRole === 1)}
              >
                User
              </Button>
              <Button
                size="small"
                variant={userRole === 2 ? "contained" : "outlined"}
                onClick={() => {
                  setUserRole(2);
                  setPage(1);
                }}
                sx={roleFilterButtonSx(userRole === 2)}
              >
                Broker
              </Button>
            </Stack>
          </Stack>
          <Chip
            label="No auth required"
            size="small"
            sx={{ height: 26, fontSize: 12, bgcolor: ui.panelMuted, color: ui.inkMuted }}
          />
        </Box>

        {error ? (
          <Alert severity="error" sx={{ m: 1 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderCellSx}>Property</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Owner</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Type</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Location</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Price</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Listing</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Date</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ ...tableBodyCellSx, textAlign: "center" }}>
                        No announcements found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow hover key={row.id} sx={tableRowHoverSx}>
                        <TableCell sx={tableBodyCellSx}>{row.title}</TableCell>
                        <TableCell sx={tableBodyCellSx}>{row.ownerName}</TableCell>
                        <TableCell sx={tableBodyCellSx}>{row.type}</TableCell>
                        <TableCell sx={tableBodyCellSx}>{row.location}</TableCell>
                        <TableCell sx={tableBodyCellSx}>{row.price}</TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Chip label={row.listing} size="small" sx={{ height: 26, fontSize: 13, ...statusChipSx(row.listing) }} />
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>{row.dateListed}</TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <IconButton size="small" aria-label="View" onClick={() => openDetail(row.id)}>
                            <RemoveRedEyeOutlinedIcon sx={{ fontSize: 18, color: ui.iconView }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent={{ xs: "center", sm: "flex-end" }} flexWrap="wrap" spacing={1} sx={{ p: 1.2 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Typography sx={{ fontSize: 14, alignSelf: "center", color: ui.inkMuted }}>
                Page {page} {total ? `(${total} total)` : ""}
              </Typography>
              <Button size="small" disabled={!canGoNext} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </Stack>
          </>
        )}
      </Paper>

      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={closeDetail}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 480, md: 560 },
            maxWidth: "100vw",
            p: 2,
            bgcolor: ui.surface,
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 600, color: ui.ink }}>Guest announcement details</Typography>
          <IconButton onClick={closeDetail} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
        {detailLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : detailError ? (
          <Alert severity="error">{detailError}</Alert>
        ) : (
          <AnnouncementDetailPanel announcement={detailRecord} />
        )}
      </Drawer>
    </>
  );
}
